import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';

import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';

import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';

import LogoContainer from 'containers/LogoContainer';
import { formatCurrencyARS } from 'helpers/currency';
import './NavbarMan.scss';

const NavbarMan = ({
  cartSize = 0,
  cartTotalFormatted,
  categories = [],
  subCategories = [],
  user,
  logout,
  openLoginModal,
  products = [],
  setSelectedProduct,
  selectedProduct,
  onSearchProductChange,
  searchProduct = '',
  onSubmit,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const rootRef = useRef(null);
  const navRef  = useRef(null);
  const catsLiRef = useRef(null);
  const menuTimer = useRef(null);

  // Evita scroll de fondo cuando el drawer está abierto
  useLayoutEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Señalamos que está activo este navbar para anular el padding-top global de D5
  useEffect(() => {
    document.body.classList.add('has-navbar-man');
    return () => document.body.classList.remove('has-navbar-man');
  }, []);

  // Altura del header (scoped a este componente)
  useEffect(() => {
    const setH = () => {
      const h = navRef.current ? navRef.current.offsetHeight : 72;
      if (rootRef.current) {
        rootRef.current.style.setProperty('--man-nav-h', `${h}px`);
      }
    };
    setH();

    const onResize = () => setH();
    window.addEventListener('resize', onResize);

    let ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(setH);
      if (navRef.current) ro.observe(navRef.current);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  // Dropdown categorías
  const openDeskMenu  = () => {
    if (menuTimer.current) clearTimeout(menuTimer.current);
    setIsMenuOpen(true);
  };
  const closeDeskMenu = (delay = 120) => {
    if (menuTimer.current) clearTimeout(menuTimer.current);
    menuTimer.current = setTimeout(() => setIsMenuOpen(false), delay);
  };

  // Cerrar al click fuera
  useEffect(() => {
    const onDocClick = (e) => {
      if (!catsLiRef.current) return;
      if (catsLiRef.current.contains(e.target)) return;
      setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const subsByCatId = useMemo(() => {
    const m = {};
    (subCategories ?? []).forEach(sc => {
      if (!m[sc.category_id]) m[sc.category_id] = [];
      m[sc.category_id].push(sc);
    });
    return m;
  }, [subCategories]);

  const renderSearchOption = (opt) => {
    if (typeof opt === 'string') {
      return (
        <div className="search-item">
          <div className="search-item__img" />
          <div className="search-item__info">
            <div className="search-item__title">{opt}</div>
          </div>
        </div>
      );
    }
    const price = opt?.price ?? 0;
    return (
      <div className="search-item" data-testid={`search-item-${opt?.id || 's'}`}>
        <div className="search-item__img">
          <img src={opt?.image1 || '/images/placeholder.png'} alt={opt?.name || ''} loading="lazy" />
        </div>
        <div className="search-item__info">
          <div className="search-item__title" title={opt?.name || ''}>{opt?.name || ''}</div>
          <div className="search-item__price">
            {opt?.sale ? (
              <>
                <span className="search-item__price--sale">{formatCurrencyARS(opt.sale)}</span>
                <span className="search-item__price--old">{formatCurrencyARS(price)}</span>
              </>
            ) : (<span>{formatCurrencyARS(price)}</span>)}
          </div>
        </div>
      </div>
    );
  };

  const handleSearchSubmit = () => {
    if (typeof onSubmit === 'function') onSubmit();
  };

  return (
    <div className="navbar-man" ref={rootRef}>
      <nav className="man-navbar" ref={navRef}>
        <div className="man-container">
          <div className="man-inner">
            <div className="man-left">
              <Link to="/" className="man-logo" aria-label="Inicio">
                <LogoContainer />
              </Link>

              <ul className="man-navLinks">
                <li><NavLink exact to="/" activeClassName="active">Home</NavLink></li>

                <li
                  className="has-dropdown"
                  ref={catsLiRef}
                  onMouseEnter={openDeskMenu}
                  onMouseLeave={() => closeDeskMenu()}
                >
                  <button type="button" className="linklike">Categorías</button>
                  <div className={`man-menuDropdown ${isMenuOpen ? 'open' : ''}`} role="menu">
                    <ul className="man-menuList">
                      {categories.map(cat => (
                        <li key={cat.id} className="man-menuItem">
                          <NavLink
                            to={`/productos/${cat.code}`}
                            className="man-catLink"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {cat.name}{subsByCatId[cat.id]?.length ? <span className="chev">›</span> : null}
                          </NavLink>
                          {subsByCatId[cat.id]?.length ? (
                            <ul className="man-subList">
                              {subsByCatId[cat.id].map(sub => (
                                <li key={sub.id}>
                                  <NavLink
                                    to={`/productos/${cat.code}/${sub.code}`}
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    {sub.name}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>

                <li><NavLink to="/ofertas" activeClassName="active">Ofertas</NavLink></li>
                <li><NavLink to="/contacto" activeClassName="active">Contacto</NavLink></li>
              </ul>
            </div>

            {/* Search inline minimal */}
            <div className="man-searchShell">
              <div className="man-searchBar">
                <SearchIcon className="man-searchIcon" />
                <div className="man-searchInput">
                  <Autocomplete
                    key="autocomplete-desktop"
                    options={products}
                    freeSolo
                    filterOptions={(opts) => opts}
                    onChange={(e, product) => setSelectedProduct(e, product)}
                    value={selectedProduct ?? null}
                    onInputChange={onSearchProductChange}
                    inputValue={searchProduct}
                    getOptionSelected={(o, p) => o?.id === p?.id}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name ?? ''))}
                    renderOption={renderSearchOption}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar..."
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="man-right">
              <Link to="/carrito" className="man-iconLink man-icon--bare man-cartBtn" aria-label="Carrito">
                <ShoppingCartOutlinedIcon />
                {Number(cartSize) > 0 ? <span className="man-cartBadge">{cartSize}</span> : null}
                {Number(cartSize) > 0 ? <span className="cart-total-pill">{cartTotalFormatted}</span> : null}
              </Link>

              {!user ? (
                <button className="man-iconLink man-icon--bare" onClick={openLoginModal} aria-label="Iniciar sesión">
                  <PersonOutlineIcon />
                </button>
              ) : (
                <button className="man-iconLink man-icon--bare" onClick={logout} aria-label="Cerrar sesión">
                  <ExitToAppIcon />
                </button>
              )}
            </div>

            <div className="man-compact">
              <button className="iconbtn iconbtn--bare" aria-label="Abrir menú" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </button>

              <Link to="/" className="man-logoMobile" aria-label="Inicio"><LogoContainer /></Link>

              <div className="man-compactActions">
                <button className="iconbtn iconbtn--bare" aria-label="Buscar" onClick={() => setMobileSearchOpen(s => !s)}>
                  <SearchIcon />
                </button>
                <Link to="/carrito" className="iconbtn iconbtn--bare man-cartBtn" aria-label="Carrito">
                  <ShoppingCartOutlinedIcon />
                  {Number(cartSize) > 0 ? <span className="man-cartBadge">{cartSize}</span> : null}
                  {Number(cartSize) > 0 ? <span className="cart-total-pill">{cartTotalFormatted}</span> : null}
                </Link>
                {!user ? (
                  <button className="iconbtn iconbtn--bare" aria-label="Iniciar sesión" onClick={openLoginModal}>
                    <PersonOutlineIcon />
                  </button>
                ) : (
                  <button className="iconbtn iconbtn--bare" aria-label="Cerrar sesión" onClick={logout}>
                    <ExitToAppIcon />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* 🔁 Importante: el buscador móvil VA ANTES del offset para que no quede “pozo” */}
      {mobileSearchOpen && (
        <div className="man-mobileSearch">
          <div className="man-searchBar">
            <SearchIcon className="man-searchIcon" />
            <div className="man-searchInput">
              <Autocomplete
                key="autocomplete-mobile"
                options={products}
                freeSolo
                filterOptions={(opts) => opts}
                onChange={(e, product) => setSelectedProduct(e, product)}
                value={selectedProduct ?? null}
                onInputChange={onSearchProductChange}
                inputValue={searchProduct}
                getOptionSelected={(o, p) => o?.id === p?.id}
                getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name ?? ''))}
                renderOption={renderSearchOption}
                noOptionsText="Sin resultados"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar..."
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}

      {drawerOpen && <div className="man-overlay" onClick={() => setDrawerOpen(false)} />}
      <aside className={`man-drawer${drawerOpen ? ' open' : ''}`} role="dialog" aria-modal="true">
        <button className="man-drawerClose" onClick={() => setDrawerOpen(false)} aria-label="Cerrar menú">
          <CloseIcon />
        </button>
        <ul className="man-drawerList">
          <li><NavLink exact to="/" onClick={() => setDrawerOpen(false)}>Home</NavLink></li>
          {categories.map((cat) => (
            <li key={cat.id}>
              <NavLink to={`/productos/${cat.code}`} onClick={() => setDrawerOpen(false)}>{cat.name}</NavLink>
              {(subCategories || []).filter(s => s.category_id === cat.id).length ? (
                <ul className="man-drawerSubs">
                  {(subCategories || []).filter(s => s.category_id === cat.id).map(sub => (
                    <li key={sub.id}>
                      <NavLink to={`/productos/${cat.code}/${sub.code}`} onClick={() => setDrawerOpen(false)}>
                        {sub.name}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
          <li><NavLink to="/ofertas"  onClick={() => setDrawerOpen(false)}>Ofertas</NavLink></li>
          <li><NavLink to="/contacto" onClick={() => setDrawerOpen(false)}>Contacto</NavLink></li>
        </ul>
      </aside>
    </div>
  );
};

export default NavbarMan;
